/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect } from 'react';
import styled from 'styled-components';
import UAEventTracking from 'modules/tracking/UAEventTracking';
import { formatReact } from 'modules/helpers/strings';
import Countdown from 'components/countdown';
import {
    GetExpectedDeliverySummaryForProductPageQueryKey,
    useGetExpectedDeliverySummaryForProductPageQuery
} from 'autogen/swagger/all/hooks';
import queryClient from 'setup/react-query/queryClient';
import type { IStockStatusViewModelMaybe, StockStatusViewModel } from 'autogen/swagger/all';
import {
    type StockStatusWithPreciseDeliveryAndModalViewModel,
    StockStatusViewModelDiscriminator as getIStockStatusViewModelDiscriminator
} from 'autogen/swagger/all';
import { InlineButtonWithSpinner } from 'components/generic/InlineButton';
import { Typography } from 'components/generic/typography';
import { FontLineHeightNormal, FontWeightSemiBold, Spacing8 } from 'autogen/design-tokens/tokens';
import format from 'helpers/translations';
import Bullet from 'components/Bullet';
import BulletItem from 'components/BulletItem';
import { useProductPageExpectedDeliveryQuickView } from './shared/hooks/useProductPageExpectedDeliveryQuickView';

const StyledDot = styled(Bullet)`
    margin-right: ${Spacing8};
`;

const Container = styled(Typography.Body)`
    margin-bottom: 0;
    line-height: ${FontLineHeightNormal};
`;

const StyledCountdown = styled(Countdown)`
    width: auto;
    font-weight: ${FontWeightSemiBold};
`;

const CountdownContainer = styled.span`
    display: block;
`;

let zipCodeMemory: string;

export function SimpleHandle({ model }: { model: StockStatusViewModel }): JSX.Element {
    const { checkMark: checkmarkType, text } = model;

    return (
        <Container size="S">
            {checkmarkType && <StyledDot />}
            <span>{format(text)}</span>
        </Container>
    );
}

function Handle({ model }: { model: StockStatusWithPreciseDeliveryAndModalViewModel }): JSX.Element | null {
    const { expectedDeliverySummary } = model;

    const toggle = useProductPageExpectedDeliveryQuickView();

    const { countdown, mainText, buttonText, zipCodeWithSource } = expectedDeliverySummary;

    const { zipCode } = zipCodeWithSource || {};

    async function openQuickView() {
        if (zipCode && zipCode !== zipCodeMemory) {
            zipCodeMemory = zipCode;
            UAEventTracking({
                eventCategory: 'expectedDelivery',
                eventAction: 'openModal',
                eventLabel: 'zipSearch'
            });
        } else {
            UAEventTracking({
                eventCategory: 'expectedDelivery',
                eventAction: 'openModal',
                eventLabel: 'summary'
            });
        }

        toggle(true);
    }

    return (
        <BulletItem>
            <>
                {buttonText ? (
                    formatReact(mainText)(
                        <InlineButtonWithSpinner type="button" onClick={openQuickView}>
                            {buttonText}
                        </InlineButtonWithSpinner>
                    )
                ) : (
                    <span>{format(mainText)}</span>
                )}
                {countdown && (
                    <CountdownContainer>
                        {countdown.orderBeforeText}{' '}
                        <StyledCountdown
                            countdown={{
                                finishDateTime: countdown.cutoff,
                                sizeType: 'XS',
                                showTimeUnits: true
                            }}
                        />
                    </CountdownContainer>
                )}
            </>
        </BulletItem>
    );
}

/**
 * For ProductPage
 */
function StockStatusExpectedDeliverySummary({
    model
}: {
    model: StockStatusWithPreciseDeliveryAndModalViewModel;
}) {
    const { expectedDeliverySummary: initialSummaryModel, productId, isSubscription } = model;

    const { data: summaryModel } = useGetExpectedDeliverySummaryForProductPageQuery(
        { productId, isSubscription },
        { initialData: initialSummaryModel }
    );

    const modelWithUpdatedSummaryModel = { ...model, expectedDeliverySummary: summaryModel || initialSummaryModel };
    const { validForMilliseconds } = modelWithUpdatedSummaryModel.expectedDeliverySummary;

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (validForMilliseconds && validForMilliseconds > 0) {
            timer = setTimeout(async () => {
                queryClient.invalidateQueries({ queryKey: [GetExpectedDeliverySummaryForProductPageQueryKey] });
            }, validForMilliseconds);
        }
        return () => {
            clearTimeout(timer);
        };
    }, [validForMilliseconds, productId, isSubscription]);

    return <Handle model={modelWithUpdatedSummaryModel} />;
}

export type StockStatusProps = {
    model: IStockStatusViewModelMaybe;
};

export default function StockStatus({ model, ...restProps }: StockStatusProps) {
    return getIStockStatusViewModelDiscriminator(
        model,
        (modelStockStatus) => <SimpleHandle {...restProps} model={modelStockStatus} />,
        (modelWithExpectedDelivery) => (
            <StockStatusExpectedDeliverySummary {...restProps} model={modelWithExpectedDelivery} />
        ),
        (modelStockStatusDelivery) => <SimpleHandle {...restProps} model={modelStockStatusDelivery} />,
        () => {
            console.error('invalid stock type');
            return null;
        }
    );
}
